import React, { useState, useEffect, useCallback } from 'react';
import { UtensilsCrossed, Check, X, LogOut, Delete, RefreshCw, ListChecks, KeyRound } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const MEALS = ['lunch', 'dinner', 'snacks'];

// VEEPEE brand colors
const BRAND_BLUE = 'rgb(12, 59, 189)';
const BRAND_RED = 'rgb(202, 19, 19)';

const mealColor = (m) => ({
  lunch: 'text-red-400', dinner: 'text-blue-400', snacks: 'text-emerald-400',
}[m] || 'text-gray-700');

// Full-screen kiosk for cafeteria staff: enter the 4-digit PIN a worker shows,
// see who they are + which meal, and confirm collection.
const CafeteriaConfirm = () => {
  const { token, user, logout } = useAuth();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [pin, setPin] = useState('');
  const [result, setResult] = useState(null); // lookup result
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  // Extra portions handed over on top of the booked meal.
  const [extra, setExtra] = useState(0);

  const [view, setView] = useState('verify'); // 'verify' | 'lists'
  const [counts, setCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const loadCounts = useCallback(async () => {
    setCountsLoading(true);
    try {
      const res = await api.get('/cafeteria/counts', {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: today },
      });
      setCounts(res.data);
    } catch (e) {
      setCounts(null);
    } finally {
      setCountsLoading(false);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (view === 'lists') loadCounts();
    // eslint-disable-next-line
  }, [view]);

  // Refresh the lists after a successful collection so counts stay current.
  const reset = () => { setPin(''); setResult(null); setError(''); setConfirmed(false); setExtra(0); };

  const press = (d) => {
    setError('');
    if (pin.length < 4) setPin((p) => p + d);
  };
  const backspace = () => { setError(''); setPin((p) => p.slice(0, -1)); };

  const lookup = async () => {
    if (pin.length !== 4) { setError('Enter the 4-digit PIN'); return; }
    setBusy(true); setError(''); setResult(null); setConfirmed(false);
    try {
      const res = await api.post('/cafeteria/lookup', { pin }, authHeader);
      if (res.data?.found) {
        setResult(res.data);
      } else {
        setError('No matching PIN for today');
        setPin(''); // clear so a new PIN can be entered straight away
      }
    } catch (e) {
      setError(e.response?.data?.error || 'No matching PIN for today');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true); setError('');
    try {
      // Extra portions are recorded here, at hand-over — they are not booked in
      // advance, so they never reach the vendor headcount, only the daily report.
      await api.post('/cafeteria/confirm', { pin, extra }, authHeader);
      setConfirmed(true);
      setCounts(null); // force refresh of lists next time it's opened
      setTimeout(reset, 2500);
    } catch (e) {
      if (e.response?.status === 409) {
        setError('This PIN was already collected.');
      } else {
        setError(e.response?.data?.error || 'Failed to confirm');
      }
    } finally {
      setBusy(false);
    }
  };

  // Give extra food to someone who has already collected — they come back for
  // seconds, so there is no PIN left to confirm.
  const addExtraOnly = async () => {
    if (extra < 1) { setError('Set how many extra portions first.'); return; }
    setBusy(true); setError('');
    try {
      await api.post('/cafeteria/extra-portion', { pin, count: extra }, authHeader);
      setConfirmed(true);
      setCounts(null);
      setTimeout(reset, 2500);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to add extra');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-white flex flex-col items-center justify-center p-6" style={{ backgroundColor: BRAND_BLUE }}>
      <div className="absolute top-4 right-4 flex items-center gap-3 text-gray-400 text-sm">
        <span>{user?.name || 'Cafeteria'}</span>
        <button onClick={logout} className="flex items-center gap-1 hover:text-white">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <UtensilsCrossed className="w-8 h-8" style={{ color: BRAND_RED }} />
        <h1 className="text-3xl font-bold">Cafeteria</h1>
      </div>

      {/* View toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setView('verify')}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-white"
          style={{ backgroundColor: view === 'verify' ? BRAND_BLUE : BRAND_RED }}
        ><KeyRound className="w-4 h-4" /> Verify PIN</button>
        <button
          onClick={() => setView('lists')}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-white"
          style={{ backgroundColor: view === 'lists' ? BRAND_BLUE : BRAND_RED }}
        ><ListChecks className="w-4 h-4" /> Today's Meals</button>
      </div>

      {view === 'lists' ? (
        <div className="w-full max-w-5xl">
          <div className="flex items-center justify-between mb-3">
            <div className="text-gray-300 text-sm">{today}</div>
            <button onClick={loadCounts} className="flex items-center gap-1 text-gray-300 hover:text-white text-sm">
              <RefreshCw className={`w-4 h-4 ${countsLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          {countsLoading ? (
            <div className="text-center text-gray-400 py-10">Loading…</div>
          ) : !counts ? (
            <div className="text-center text-gray-400 py-10">No data.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {MEALS.map((m) => {
                const c = counts.meals?.[m] || {};
                const people = c.users || [];
                return (
                  <div key={m} className="bg-gray-800 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className={`text-xl font-bold capitalize ${mealColor(m)}`}>{m}</h2>
                      <div className="text-right text-xs text-gray-400">
                        <div className="text-2xl font-bold text-white">{c.count || 0}</div>
                        to feed
                      </div>
                    </div>
                    <div className="flex gap-2 text-xs mb-3">
                      <span className="px-2 py-0.5 rounded bg-green-900/40 text-green-300">Collected {c.collected || 0}</span>
                      <span className="px-2 py-0.5 rounded bg-red-900/40 text-red-300">Pending {c.not_collected || 0}</span>
                    </div>
                    <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-gray-700">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-700/80">
                          <tr className="text-left text-gray-300">
                            <th className="px-3 py-2">#</th>
                            <th className="px-3 py-2">Name</th>
                            <th className="px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {people.length === 0 ? (
                            <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-500">Nobody.</td></tr>
                          ) : (
                            people.map((u, i) => (
                              <tr key={u.id} className="border-t border-gray-700/60">
                                <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                                <td className="px-3 py-2 text-white">{u.name}</td>
                                <td className="px-3 py-2">
                                  {u.collected ? (
                                    <span className="px-2 py-0.5 rounded text-xs bg-green-900/40 text-green-300">Collected</span>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-300">{u.collect_status || 'Not yet'}</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      <div className="w-full max-w-md rounded-2xl shadow-xl p-8" style={{ backgroundColor: BRAND_RED }}>
        {confirmed ? (
          <div className="text-center py-10">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-500 flex items-center justify-center mb-4">
              <Check className="w-12 h-12 text-white" />
            </div>
            <div className="text-2xl font-bold mb-1">Confirmed</div>
            <div className="text-gray-300">{result?.user_name} — {result?.meal}</div>
          </div>
        ) : result ? (
          <div className="text-center">
            <div className="text-gray-400 text-sm mb-1">PIN {result.pin}</div>
            <div className="text-3xl font-bold mb-1">{result.user_name}</div>
            <div className="text-gray-400 capitalize mb-4">{(result.user_role || '').replace(/_/g, ' ')}</div>
            <div className={`text-2xl font-semibold capitalize mb-6 ${mealColor(result.meal)}`}>
              {result.meal}
            </div>
            {result.status === 'collected' ? (
              <div className="bg-red-900/40 border border-red-700 text-red-300 rounded-lg px-4 py-3 mb-4">
                Already collected{result.collected_at ? ` at ${new Date(result.collected_at).toLocaleTimeString()}` : ''}.
              </div>
            ) : null}

            {/* Extra portions — counted in the daily report, not in the vendor forecast */}
            <div className="bg-black/20 rounded-xl px-4 py-3 mb-4">
              <div className="text-sm text-gray-300 mb-2">Extra food</div>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setExtra((n) => Math.max(0, n - 1))}
                  disabled={extra === 0}
                  className="w-14 h-14 rounded-xl bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-3xl font-bold"
                  aria-label="One less extra portion"
                >−</button>
                <div className="w-16 text-4xl font-bold tabular-nums">{extra}</div>
                <button
                  onClick={() => setExtra((n) => Math.min(10, n + 1))}
                  className="w-14 h-14 rounded-xl bg-gray-700 hover:bg-gray-600 text-3xl font-bold"
                  aria-label="One more extra portion"
                >+</button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={reset}
                className="flex-1 py-4 rounded-xl bg-gray-700 hover:bg-gray-600 text-lg font-medium flex items-center justify-center gap-2"
              ><X className="w-5 h-5" /> Cancel</button>
              {result.status !== 'collected' ? (
                <button
                  onClick={confirm}
                  disabled={busy}
                  className="flex-1 py-4 rounded-xl bg-green-600 hover:bg-green-500 disabled:opacity-50 text-lg font-bold flex items-center justify-center gap-2"
                ><Check className="w-5 h-5" /> Confirm{extra > 0 ? ` +${extra}` : ''}</button>
              ) : (
                <button
                  onClick={addExtraOnly}
                  disabled={busy || extra < 1}
                  className="flex-1 py-4 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-lg font-bold flex items-center justify-center gap-2"
                ><Check className="w-5 h-5" /> Add {extra} extra</button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="w-14 h-16 rounded-xl bg-gray-700 flex items-center justify-center text-3xl font-bold">
                  {pin[i] ? '•' : ''}
                </div>
              ))}
            </div>
            {error && <div className="text-center text-red-400 mb-4">{error}</div>}
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <button
                  key={d}
                  onClick={() => press(String(d))}
                  className="py-5 rounded-xl text-white text-2xl font-semibold"
                  style={{ backgroundColor: BRAND_BLUE }}
                >{d}</button>
              ))}
              <button onClick={backspace} className="py-5 rounded-xl text-white flex items-center justify-center" style={{ backgroundColor: BRAND_BLUE }}>
                <Delete className="w-6 h-6" />
              </button>
              <button onClick={() => press('0')} className="py-5 rounded-xl text-white text-2xl font-semibold" style={{ backgroundColor: BRAND_BLUE }}>0</button>
              <button
                onClick={lookup}
                disabled={busy || pin.length !== 4}
                className="py-5 rounded-xl text-white disabled:opacity-40 text-xl font-bold"
                style={{ backgroundColor: BRAND_BLUE }}
              >Go</button>
            </div>
          </>
        )}
      </div>
      )}

      {view === 'verify' && (
        <p className="text-gray-500 text-sm mt-6">Ask the employee for their 4-digit meal PIN.</p>
      )}
    </div>
  );
};

export default CafeteriaConfirm;
